import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Select, Button, Typography, Divider, DatePicker } from "antd"
import {
    InfoCircleOutlined,
    CheckOutlined,
    SettingOutlined,
    DownOutlined,
    CalendarOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    UserAddOutlined,
    FileTextOutlined,
    TagOutlined
} from "@ant-design/icons"
import { DrawerWrapper } from "../../../../../components"
import { create_schedule_pretrip_request } from "../../../../../services/redux/actions/drivers"
import dayjs from "dayjs"

const { Text } = Typography
const { RangePicker } = DatePicker

// Helpers
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const getWeekStart = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const dayOfWeek = d.getDay() // 0 = Sun
    d.setDate(d.getDate() - dayOfWeek)
    return d
}

const getDaysInWeek = (date) => {
    const sunday = getWeekStart(date)
    return Array.from({ length: 7 }, (_, i) => {
        const day = new Date(sunday)
        day.setDate(sunday.getDate() + i)
        return day
    })
}

// All "Sundays" that start a week, overlapping with start..end range
const getWeekStartsInPeriod = (start, end) => {
    const weeks = []
    let cursor = getWeekStart(start)
    const lastWeekStart = getWeekStart(end)
    while (cursor <= lastWeekStart) {
        weeks.push(new Date(cursor))
        cursor = new Date(cursor)
        cursor.setDate(cursor.getDate() + 7)
    }
    return weeks
}

// Nearest Sunday on/after the given date (if the date itself
// is Sunday, return that date)
const getNextSunday = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const day = d.getDay() // 0 = Sun
    if (day === 0) return d
    d.setDate(d.getDate() + (7 - day))
    return d
}

// All dates (daily) between start..end, inclusive
// Used to generate ALL dates in the Selected Period during publish,
// because the API payload is sent for the entire period, not just the highlighted grid dates.
const getDaysInRange = (start, end) => {
    const days = []
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate())
    while (cursor <= last) {
        days.push(new Date(cursor))
        cursor.setDate(cursor.getDate() + 1)
    }
    return days
}

// const formatDate = (date) =>
//     date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })

// Format date as "YYYY-MM-DD" based on the user's LOCAL device timezone (not UTC).
// Avoids using date.toISOString().split("T")[0] since it converts to UTC
// first — for positive timezones (e.g. UTC+), local midnight could shift
// to the previous day. dayjs(date).format(), like native Date, automatically
// follows the device/browser timezone — so this is dynamic rather than hardcoded.
// Used for internal keys (dayStatus) and the API payload to ensure timezone consistency.
const toISODateString = (date) => dayjs(date).format("YYYY-MM-DD")

const formatDateShort = (date) =>
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })

const isSameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()

const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6

// Compare dates (ignoring time) — true if `date` is before `today`
const isBeforeToday = (date, today) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    return d < t
}

// Compare dates (ignoring time) — true if `date` is between start & end (inclusive)
const isWithinPeriod = (date, start, end) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate())
    return d >= s && d <= e
}

// Status constants — used in internal state & publish payload
export const STATUS = {
    WORKING: "WORKING",
    OFF: "OFF",
    LEAVE: "LEAVE",
}

// Default Rule options definition (shown in custom dropdown)
const RULE_OPTIONS = [
    {
        value: "weekdays_working",
        title: "Mon–Fri Working, Sat–Sun Off",
        subtitle: "Monday to Friday as Working Day, Saturday & Sunday as Off Day",
        icon: "calendar",
    },
    {
        value: "all_working",
        title: "All Days = Working Day",
        subtitle: "Every day of the week is set as Working Day",
        icon: "calendar",
    },
    {
        value: "all_off",
        title: "All Days = Off Day",
        subtitle: "Every day of the week is set as Off Day",
        icon: "calendar",
    },
    {
        value: "custom",
        title: "Custom Working Days",
        subtitle: "Set your own working and off days",
        icon: "gear",
    },
]

// Default status based on the selected default rule (including custom)
const getDefaultStatusForRule = (date, rule, customWorkingDays) => {
    const dayName = DAYS[date.getDay()]

    switch (rule) {
        case "all_working":
            return STATUS.WORKING
        case "all_off":
            return STATUS.OFF
        case "custom":
            return customWorkingDays.includes(dayName) ? STATUS.WORKING : STATUS.OFF
        case "weekdays_working":
        default:
            return isWeekend(date) ? STATUS.OFF : STATUS.WORKING
    }
}

const SetScheduleModal = ({ isOpen = false, setIsOpen = () => {} }) => {

    const dispatch = useDispatch()

    const today = new Date() // June 2, 2026 (as in mock image)

    const drivers = useSelector((state) => state.containers.v3.drivers.byID)
    const { startTime, endTime, selectedDRVID: drvid } = useSelector((state) => state.containers.v3.drivers)

    // Raw period from redux (if any). If none, fallback to the current week.
    const rawPeriodStart = startTime ? dayjs(startTime).toDate() : getWeekStart(today)
    const rawPeriodEnd = endTime ? dayjs(endTime).toDate() : getDaysInWeek(today)[6]

    // If the entire period from redux is in the past (end date before today),
    // default to "today until nearest Sunday" rather than the expired period.
    const isSavedPeriodExpired = isBeforeToday(rawPeriodEnd, today)
    const defaultPeriodStart = isSavedPeriodExpired ? today : rawPeriodStart
    const defaultPeriodEnd = isSavedPeriodExpired ? getNextSunday(today) : rawPeriodEnd

    // Selected Period is now a local state that CAN BE CHANGED directly by the user in this drawer
    // (not just an auto-computed/hardcoded value from redux)
    const [periodRange, setPeriodRange] = useState([defaultPeriodStart, defaultPeriodEnd])
    const periodStart = periodRange[0]
    const periodEnd = periodRange[1]

    const [selectedDate, setSelectedDate] = useState(periodStart)
    // Highlighted dates (multi-select) in the "Set status for..." grid.
    // IMPORTANT: this is NOT the list of dates sent to the API. This is only used to
    // determine which dates change status when the user clicks "Working Day" / "Off Day".
    // The dates actually sent to the API upon publish are ALL dates within the Selected Period (see handlePublish).
    const [selectedDates, setSelectedDates] = useState([periodStart])
    const [applyTo, setApplyTo] = useState([drvid])
    const [defaultRule, setDefaultRule] = useState("weekdays_working")
    const [isRuleMenuOpen, setIsRuleMenuOpen] = useState(false)

    // Index of the week currently shown in the calendar grid, relative to Selected Period
    const [viewWeekIndex, setViewWeekIndex] = useState(0)

    // Days considered Working Days when Default Rule = custom
    const [customWorkingDays, setCustomWorkingDays] = useState(["Mon", "Tue", "Wed", "Thu", "Fri"])

    // Map date to status: key = "YYYY-MM-DD", value = STATUS.WORKING | STATUS.OFF
    const [dayStatus, setDayStatus] = useState({})

    const getDayStatus = (date) => {
        const key = toISODateString(date)
        return dayStatus[key] ?? getDefaultStatusForRule(date, defaultRule, customWorkingDays)
    }

    // Set status for ALL currently highlighted dates (selectedDates).
    // This is called when the user clicks the "Working Day" / "Off Day" button.
    const setStatusForSelected = (status) => {
        setDayStatus((prev) => {
            const next = { ...prev }
            selectedDates.forEach((date) => {
                const key = toISODateString(date)
                next[key] = status
            })
            return next
        })
    }

    // Toggle one date in selectedDates (click to add/remove highlight)
    const toggleSelectedDate = (day) => {
        setSelectedDates((prev) => {
            const exists = prev.some((d) => isSameDay(d, day))
            if (exists) {
                // at least 1 date must be selected
                if (prev.length === 1) return prev
                return prev.filter((d) => !isSameDay(d, day))
            }
            return [...prev, day]
        })
        setSelectedDate(day)
    }

    const toggleCustomDay = (day) => {
        setCustomWorkingDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
        )
    }

    const handleSelectRule = (value) => {
        setDefaultRule(value)
        setIsRuleMenuOpen(false)
    }

    const selectedRuleOption = RULE_OPTIONS.find((r) => r.value === defaultRule)

    // All weeks (Sunday-start) that overlap with the selected period
    // (periodStart/periodEnd have been computed above, including the 1-week fallback if the saved period has expired)
    const weekStartsInPeriod = getWeekStartsInPeriod(periodStart, periodEnd)
    const hasMultipleWeeks = weekStartsInPeriod.length > 1
    const clampedWeekIndex = Math.min(viewWeekIndex, Math.max(weekStartsInPeriod.length - 1, 0))
    const activeWeekStart = weekStartsInPeriod[clampedWeekIndex] ?? getWeekStart(selectedDate)

    // If the timeframe in redux changes (e.g. user changes filters on the Driver Report page),
    // sync the default Selected Period again — but the user can still manually override it afterwards.
    useEffect(() => {
        setPeriodRange([defaultPeriodStart, defaultPeriodEnd])
    }, [startTime, endTime])

    // Each time the Selected Period changes (from redux OR manually edited via RangePicker),
    // reset the displayed week and highlight the date to the beginning of the new period.
    useEffect(() => {
        setViewWeekIndex(0)
        setSelectedDate(periodStart)
        setSelectedDates([periodStart])
    }, [periodStart.getTime(), periodEnd.getTime()])

    // Called when the user changes the Selected Period directly via RangePicker
    const handlePeriodChange = (dates) => {
        if (!dates || !dates[0] || !dates[1]) return
        setPeriodRange([dates[0].toDate(), dates[1].toDate()])
    }

    // Limit RangePicker: cannot select a date before today
    const isPeriodDateDisabled = (current) => {
        if (!current) return false
        return isBeforeToday(current.toDate(), today)
    }

    // weekDays is now based on the week being browsed, not directly from selectedDate
    const weekDays = getDaysInWeek(activeWeekStart)

    const canGoPrevWeek = clampedWeekIndex > 0
    const canGoNextWeek = clampedWeekIndex < weekStartsInPeriod.length - 1

    const goToPrevWeek = () => {
        if (!canGoPrevWeek) return
        setViewWeekIndex((i) => Math.max(0, i - 1))
    }

    const goToNextWeek = () => {
        if (!canGoNextWeek) return
        setViewWeekIndex((i) => Math.min(weekStartsInPeriod.length - 1, i + 1))
    }

    // Combined status of all dates currently highlighted in the grid.
    // null means "Mixed" (statuses differ among the highlighted dates)
    const selectedStatuses = selectedDates.map((d) => getDayStatus(d))
    const isStatusMixed = new Set(selectedStatuses).size > 1
    const multiSelectedStatus = isStatusMixed ? null : selectedStatuses[0]

    // Order of highlighted dates, used for the "Set status for ..." text
    const orderedSelectedDates = [...selectedDates].sort((a, b) => a - b)

    const orderedDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    const orderedCustomWorkingDays = orderedDays.filter((d) => customWorkingDays.includes(d))
    const orderedCustomOffDays = orderedDays.filter((d) => !customWorkingDays.includes(d))

    const closeHDLR = () => {
        setIsOpen(false)
    }

    const handlePublish = () => {
        const drvids = applyTo

        const allPeriodDates = getDaysInRange(periodStart, periodEnd).filter(
            (day) => !isBeforeToday(day, today)
        )

        const schedules = allPeriodDates.map((day) => ({
            date: toISODateString(day),
            status: getDayStatus(day),
        }))

        dispatch(
            create_schedule_pretrip_request({
                drvids,
                schedules,
            })
        )

        setIsOpen(false)
    }

    const styles = {
        section: {
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: "14px 0",
        },
        icon: {
            fontSize: 18,
            color: "#8c8c8c",
            marginTop: 2,
            flexShrink: 0,
        },
        label: {
            fontSize: 13,
            fontWeight: 600,
            color: "#1a1a1a",
            display: "block",
            marginBottom: 4,
        },
        value: {
            fontSize: 13,
            color: "#595959",
        },
        blueValue: {
            fontSize: 13,
            color: "#1677ff",
            fontWeight: 500,
        },
        select: {
            width: "100%",
        },
        // --- Default Rule dropdown (custom, non-antd Select) ---
        ruleField: {
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            cursor: "pointer",
            padding: "2px 0",
        },
        ruleFieldText: {
            fontSize: 13,
            color: "#1677ff",
            fontWeight: 500,
            lineHeight: 1.4,
        },
        ruleChevron: (open) => ({
            fontSize: 11,
            color: "#8c8c8c",
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
        }),
        ruleDropdownPanel: {
            marginTop: 8,
            border: "1px solid #e6f4ff",
            borderRadius: 10,
            overflow: "hidden",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        },
        ruleOptionItem: (selected, isLast) => ({
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "10px 12px",
            cursor: "pointer",
            background: selected ? "#e6f4ff" : "#fff",
            borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        }),
        ruleOptionIcon: (selected) => ({
            fontSize: 15,
            color: selected ? "#1677ff" : "#8c8c8c",
            marginTop: 2,
            flexShrink: 0,
        }),
        ruleOptionTitle: (selected) => ({
            fontSize: 13,
            fontWeight: 600,
            color: selected ? "#1677ff" : "#1a1a1a",
        }),
        ruleOptionSubtitle: {
            fontSize: 11,
            color: "#8c8c8c",
            marginTop: 2,
            flexShrink: 0,
        },
        ruleOptionCheck: {
            fontSize: 14,
            color: "#1677ff",
            marginTop: 2,
            flexShrink: 0,
        },
        // --- Custom Working Days panel ---
        customPanel: {
            marginTop: 12,
            border: "1px solid #d9d9d9",
            borderRadius: 10,
            padding: 12,
        },
        customPanelHeader: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        },
        customPanelTitle: {
            fontSize: 13,
            fontWeight: 600,
            color: "#1a1a1a",
        },
        customPanelHint: {
            fontSize: 12,
            color: "#8c8c8c",
            marginTop: 4,
            marginBottom: 10,
        },
        dayChipGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 6,
        },
        dayChip: (selected) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            padding: "8px 2px",
            borderRadius: 8,
            border: selected ? "1px solid #b7eb8f" : "1px solid #d9d9d9",
            background: selected ? "#f6ffed" : "#fff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: selected ? "#389e0d" : "#595959",
        }),
        dayChipCheckBox: (selected) => ({
            width: 16,
            height: 16,
            borderRadius: 4,
            border: selected ? "none" : "1.5px solid #d9d9d9",
            background: selected ? "#52c41a" : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 10,
        }),
        customSummary: {
            background: "#e6f4ff",
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 12,
        },
        customSummaryTitle: {
            fontSize: 12,
            color: "#1677ff",
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
        },
        customSummaryList: {
            fontSize: 12,
            color: "#1677ff",
            margin: "6px 0 0 22px",
            paddingLeft: 14,
            lineHeight: 1.7,
        },
        setStatusRow: {
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 0 2px",
        },
        weekNavBtn: (disabled) => ({
            width: 22,
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 6,
            border: "1px solid #d9d9d9",
            background: disabled ? "#f5f5f5" : "#fff",
            color: disabled ? "#d9d9d9" : "#1677ff",
            cursor: disabled ? "not-allowed" : "pointer",
            fontSize: 11,
            flexShrink: 0,
        }),
        weekRangeLabel: {
            fontSize: 11,
            color: "#8c8c8c",
            marginBottom: 6,
        },
        calendarGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
            marginTop: 8,
        },
        dayHeader: {
            textAlign: "center",
            fontSize: 11,
            color: "#8c8c8c",
            fontWeight: 500,
            padding: "2px 0",
        },
        dayCell: (isSelected, isDisabled) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "6px 4px",
            borderRadius: 8,
            cursor: isDisabled ? "not-allowed" : "pointer",
            border: isSelected ? "2px solid #1677ff" : "2px solid transparent",
            background: isSelected ? "#e6f4ff" : "transparent",
            opacity: isDisabled ? 0.4 : 1,
            transition: "all 0.15s",
        }),
        dayNumber: (isSelected) => ({
            fontSize: 13,
            fontWeight: isSelected ? 700 : 500,
            color: isSelected ? "#1677ff" : "#1a1a1a",
            lineHeight: 1.2,
        }),
        dayStatusLabel: (status) => ({
            fontSize: 10,
            color: status === STATUS.WORKING ? "#52c41a" : "#ff4d4f",
            fontWeight: 500,
            marginTop: 2,
        }),
        statusToggle: {
            display: "flex",
            gap: 8,
            marginTop: 12,
        },
        toggleBtn: (active, type) => ({
            flex: 1,
            height: 38,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            border: active
                ? type === STATUS.WORKING
                    ? "2px solid #1677ff"
                    : "2px solid #d9d9d9"
                : "2px solid #d9d9d9",
            background: active && type === STATUS.WORKING ? "#1677ff" : "#fff",
            color: active && type === STATUS.WORKING ? "#fff" : "#595959",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
        }),
        infoBanner: {
            background: "#e6f4ff",
            borderRadius: 8,
            padding: "10px 12px",
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            marginTop: 8,
        },
        publishBtn: {
            width: "100%",
            height: 42,
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 14,
            marginTop: 16,
        },
    }

    return (
        <DrawerWrapper
            title={"Set Operational Schedule"}
            open={isOpen}
            onClose={closeHDLR}
            footer={
                <Button
                    type="primary"
                    style={styles.publishBtn}
                    onClick={handlePublish}
                >
                    Publish Schedule
                </Button>
            }
        >
            <div style={{ padding: "0 2px" }}>
                {/* Selected Period */}
                <div style={styles.section}>
                    <CalendarOutlined />
                    <div style={{ flex: 1 }}>
                        <Text style={styles.label}>Selected Period</Text>
                        <RangePicker
                            value={[dayjs(periodStart), dayjs(periodEnd)]}
                            onChange={handlePeriodChange}
                            disabledDate={isPeriodDateDisabled}
                            format="D MMM YYYY"
                            allowClear={false}
                            size="small"
                            variant="borderless"
                            style={{
                                marginLeft: -11,
                                padding: "0 0 0 1px",
                                fontSize: styles.blueValue.fontSize,
                                color: styles.blueValue.color,
                                fontWeight: styles.blueValue.fontWeight,
                            }}
                        />
                    </div>
                </div>

                <Divider style={{ margin: "0" }} />

                {/* Apply To */}
                <div style={styles.section}>
                    <UserAddOutlined />
                    <div style={{ flex: 1 }}>
                        <Text style={styles.label}>Apply To</Text>
                        <Select
                            style={{ ...styles.select, marginLeft: -10, width: "calc(100% + 10px)" }}
                            value={applyTo}
                            onChange={setApplyTo}
                            size="small"
                            variant="borderless"
                            mode="multiple"
                            allowClear
                            placeholder="Select drivers"
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={Object.values(drivers).map((driver) => ({
                                value: driver.drvid,
                                label: driver.name,
                            }))}
                        />
                    </div>
                </div>

                <Divider style={{ margin: "0" }} />

                {/* Default Rule (custom dropdown) */}
                <div style={styles.section}>
                    <FileTextOutlined />
                    <div style={{ flex: 1, position: "relative" }}>
                        <Text style={styles.label}>Default Rule</Text>

                        <div
                            aria-hidden="true"
                            role="button"
                            tabIndex={0}
                            style={styles.ruleField}
                            onClick={() => setIsRuleMenuOpen((prev) => !prev)}
                        >
                            <Text style={styles.ruleFieldText}>
                                {selectedRuleOption ? selectedRuleOption.title : "Select default rule"}
                            </Text>
                            <DownOutlined style={styles.ruleChevron(isRuleMenuOpen)} />
                        </div>

                        {isRuleMenuOpen && (
                            <div style={styles.ruleDropdownPanel}>
                                {RULE_OPTIONS.map((option, idx) => {
                                    const selected = option.value === defaultRule
                                    return (
                                        <div
                                            key={option.value}
                                            aria-hidden="true"
                                            role="button"
                                            tabIndex={0}
                                            style={styles.ruleOptionItem(selected, idx === RULE_OPTIONS.length - 1)}
                                            onClick={() => handleSelectRule(option.value)}
                                        >
                                            <span style={styles.ruleOptionIcon(selected)}>
                                                {option.icon === "gear" ? <SettingOutlined /> : <CalendarOutlined />}
                                            </span>
                                            <div style={{ flex: 1 }}>
                                                <Text style={styles.ruleOptionTitle(selected)}>{option.title}</Text>
                                                <div style={styles.ruleOptionSubtitle}>{option.subtitle}</div>
                                            </div>
                                            {selected && <CheckOutlined style={styles.ruleOptionCheck} />}
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {/* Custom Working Days panel — muncul kalau Default Rule = custom */}
                        {defaultRule === "custom" && (
                            <div style={styles.customPanel}>
                                <div style={styles.customPanelHeader}>
                                    <Text style={styles.customPanelTitle}>Custom Working Days</Text>
                                </div>
                                <div style={styles.customPanelHint}>
                                    Choose the scheduled Working Day.
                                </div>

                                <div style={styles.dayChipGrid}>
                                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
                                        const selected = customWorkingDays.includes(day)
                                        return (
                                            <div
                                                key={day}
                                                aria-hidden="true"
                                                role="button"
                                                tabIndex={0}
                                                style={styles.dayChip(selected)}
                                                onClick={() => toggleCustomDay(day)}
                                            >
                                                <span style={styles.dayChipCheckBox(selected)}>
                                                    {selected && <CheckOutlined />}
                                                </span>
                                                {day}
                                            </div>
                                        )
                                    })}
                                </div>

                                <div style={styles.customSummary}>
                                    <div style={styles.customSummaryTitle}>
                                        <InfoCircleOutlined style={{ marginTop: 1 }} />
                                        <span>Based on the choices:</span>
                                    </div>
                                    <ul style={styles.customSummaryList}>
                                        <li>
                                            Working Days:{" "}
                                            {orderedCustomWorkingDays.length > 0
                                                ? orderedCustomWorkingDays.join(", ")
                                                : "-"}
                                        </li>
                                        <li>
                                            Off Days:{" "}
                                            {orderedCustomOffDays.length > 0
                                                ? orderedCustomOffDays.join(", ")
                                                : "-"}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <Divider style={{ margin: "0" }} />

                {/* Selected Date */}
                <div style={{ ...styles.section, alignItems: "center" }}>
                    <CalendarOutlined />
                    <div style={{ flex: 1 }}>
                        <Text style={styles.label}>Selected Date</Text>
                        <Text style={styles.blueValue}>{formatDateShort(selectedDate)}</Text>
                    </div>
                </div>

                <Divider style={{ margin: "0" }} />

                {/* Status */}
                <div style={{ ...styles.section, alignItems: "center" }}>
                    <TagOutlined />
                    <div style={{ flex: 1 }}>
                        <Text style={styles.label}>Status</Text>
                        <span
                            style={{
                                display: "inline-block",
                                background:
                                    multiSelectedStatus === STATUS.WORKING
                                        ? "#e6f4ff"
                                        : multiSelectedStatus === STATUS.OFF
                                        ? "#fff1f0"
                                        : "#fafafa",
                                color:
                                    multiSelectedStatus === STATUS.WORKING
                                        ? "#1677ff"
                                        : multiSelectedStatus === STATUS.OFF
                                        ? "#ff4d4f"
                                        : "#8c8c8c",
                                border: `1px solid ${
                                    multiSelectedStatus === STATUS.WORKING
                                        ? "#91caff"
                                        : multiSelectedStatus === STATUS.OFF
                                        ? "#ffa39e"
                                        : "#d9d9d9"
                                }`,
                                borderRadius: 6,
                                padding: "2px 10px",
                                fontSize: 13,
                                fontWeight: 500,
                            }}
                        >
                            {multiSelectedStatus === STATUS.WORKING
                                ? "Working Day"
                                : multiSelectedStatus === STATUS.OFF
                                ? "Off Day"
                                : "Mixed"}
                        </span>
                    </div>
                </div>

                {/* Set status label — arrow here to navigate WEEKS (if period > 1 week) */}
                <div style={styles.setStatusRow}>
                    {hasMultipleWeeks && (
                        <button
                            aria-label="Previous week"
                            style={styles.weekNavBtn(!canGoPrevWeek)}
                            onClick={goToPrevWeek}
                            disabled={!canGoPrevWeek}
                        >
                            <ArrowLeftOutlined />
                        </button>
                    )}
                    <Text style={{ fontSize: 13, color: "#595959" }}>
                        Set status for
                    </Text>
                    {hasMultipleWeeks && (
                        <button
                            aria-label="Next week"
                            style={styles.weekNavBtn(!canGoNextWeek)}
                            onClick={goToNextWeek}
                            disabled={!canGoNextWeek}
                        >
                            <ArrowRightOutlined />
                        </button>
                    )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
                    <Text style={{ fontSize: 13, color: "#1677ff", fontWeight: 500 }}>
                        {orderedSelectedDates.map((d) => formatDateShort(d)).join(", ")}
                    </Text>
                </div>

                {/* Label for the week being displayed (to clarify which week is currently browsed) */}
                {hasMultipleWeeks && (
                    <div style={styles.weekRangeLabel}>
                        Week {clampedWeekIndex + 1} of {weekStartsInPeriod.length} · {formatDateShort(weekDays[0])} – {formatDateShort(weekDays[6])}
                    </div>
                )}

                {/* Calendar grid */}
                <div style={styles.calendarGrid}>
                    {DAYS.map((d) => (
                        <div key={d} style={styles.dayHeader}>
                            {d}
                        </div>
                    ))}
                    {weekDays.map((day, i) => {
                        const isSelected = selectedDates.some((d) => isSameDay(d, day))
                        const status = getDayStatus(day)
                        const isPast = isBeforeToday(day, today)
                        const isOutsidePeriod = !isWithinPeriod(day, periodStart, periodEnd)
                        const isDisabled = isPast || isOutsidePeriod
                        return (
                            <div
                                aria-hidden='true'
                                key={i}
                                style={styles.dayCell(isSelected, isDisabled)}
                                onClick={() => {
                                    if (isDisabled) return
                                    toggleSelectedDate(day)
                                }}
                            >
                                <span style={styles.dayNumber(isSelected)}>{day.getDate()}</span>
                                <span style={styles.dayStatusLabel(status)}>
                                    {status === STATUS.WORKING ? "Work" : "Off"}
                                </span>
                            </div>
                        )
                    })}
                </div>

                {/* Working Day / Off Day toggle — applies to all highlighted dates */}
                <div style={styles.statusToggle}>
                    <button
                        style={styles.toggleBtn(multiSelectedStatus === STATUS.WORKING, STATUS.WORKING)}
                        onClick={() => setStatusForSelected(STATUS.WORKING)}
                    >
                        {multiSelectedStatus === STATUS.WORKING && <CheckOutlined />}
                        Working Day
                    </button>
                    <button
                        style={styles.toggleBtn(multiSelectedStatus === STATUS.OFF, STATUS.OFF)}
                        onClick={() => setStatusForSelected(STATUS.OFF)}
                    >
                        {multiSelectedStatus === STATUS.OFF && (
                            <span
                                style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    border: "2px solid #595959",
                                    display: "inline-block",
                                }}
                            />
                        )}
                        Off Day
                    </button>
                </div>

                {/* Info banner */}
                <div style={styles.infoBanner}>
                    <InfoCircleOutlined style={{ color: "#1677ff", fontSize: 15, marginTop: 1 }} />
                    <Text style={{ fontSize: 12, color: "#1677ff" }}>
                        Driver can submit pre-trip only on scheduled working days.
                    </Text>
                </div>

                {/* Publish button */}
            </div>
        </DrawerWrapper>
    )
}

export default SetScheduleModal